"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ATTACHMENT_ERROR_CODES = exports.ATTACHMENT_ACCESSES_LIMIT = exports.ATTACHMENT_STATUS_LABELS = exports.ATTACHMENT_STATUSES = exports.actionLogSchema = exports.ACTION_LOG_MAX_EVENTS = exports.ACTION_LOG_DESCRIPTION_MAX = exports.DEFAULT_ATTACHMENT_RETENTION_DAYS = exports.ACTION_LOG_MEDIA_TYPE = exports.SCREENSHOT_MEDIA_TYPES = exports.ATTACHMENT_REJECTIONS_MAX_PER_KIND = exports.ATTACHMENT_UPLOAD_WINDOW_MINUTES = exports.ATTACHMENT_MAX_BYTES = exports.ACTION_LOG_MAX_BYTES = exports.SCREENSHOT_MAX_BYTES = exports.ATTACHMENT_KIND_LABELS = exports.ATTACHMENT_KINDS = void 0;
exports.isAttachmentKind = isAttachmentKind;
const zod_1 = require("zod");
/**
 * Allegati della segnalazione inviati dai consumer (PRD-003 US-4, story #19, DEC-010; contratto di
 * integrazione §6). Arrivano **dopo** la creazione del ticket, con un caricamento separato
 * (`POST /tickets/{codice}/allegati`, multipart), e il loro fallimento non tocca il ticket.
 */
/** Tipi di allegato: al più uno per tipo e per ticket. Il nome è anche quello della parte multipart. */
exports.ATTACHMENT_KINDS = ['schermata', 'log'];
exports.ATTACHMENT_KIND_LABELS = {
    schermata: 'Schermata',
    log: 'Log delle ultime azioni',
};
function isAttachmentKind(value) {
    return typeof value === 'string' && exports.ATTACHMENT_KINDS.includes(value);
}
/** Dimensione massima della schermata: 5 MB (DEC-010). */
exports.SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
/** Dimensione massima del log: 256 kB (DEC-010). */
exports.ACTION_LOG_MAX_BYTES = 256 * 1024;
exports.ATTACHMENT_MAX_BYTES = {
    schermata: exports.SCREENSHOT_MAX_BYTES,
    log: exports.ACTION_LOG_MAX_BYTES,
};
/** Minuti, dalla ricezione del ticket, entro cui il consumer può caricare gli allegati (DEC-010). */
exports.ATTACHMENT_UPLOAD_WINDOW_MINUTES = 15;
/**
 * Rifiuti dichiarati al più per tipo e per ticket (eventi `allegato_rifiutato`): un consumer che
 * ritenta in loop non riempie il thread. Un rifiuto con lo stesso codice del precedente dello
 * stesso tipo non si registra di nuovo.
 */
exports.ATTACHMENT_REJECTIONS_MAX_PER_KIND = 5;
/** Formati ammessi per la schermata, riconosciuti dai primi byte del file e mai dall'estensione. */
exports.SCREENSHOT_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** Tipo del log: JSON in UTF-8. */
exports.ACTION_LOG_MEDIA_TYPE = 'application/json';
/** Giorni di custodia dopo `Chiuso` o `Scartato`, default per istanza (DEC-010, da confermare col DPO). */
exports.DEFAULT_ATTACHMENT_RETENTION_DAYS = 30;
/** Lunghezza massima della descrizione di un evento del log. */
exports.ACTION_LOG_DESCRIPTION_MAX = 2000;
/** Numero massimo di eventi nel log (dieci minuti di azioni, contratto §5.2). */
exports.ACTION_LOG_MAX_EVENTS = 5000;
/**
 * Log delle ultime azioni (contratto §5.2 e §6): gli eventi dei dieci minuti precedenti l'invio,
 * raccolti sul dispositivo, ciascuno con **orario** ISO 8601 con fuso e **descrizione** testuale.
 * Oggetti chiusi: un campo in più è rifiutato, non scartato, così un consumer che allegasse dati
 * non previsti (nomi, codici fiscali, corpi di risposta) se ne accorge (contratto §5.2).
 *
 * @example { "eventi": [{ "orario": "2026-10-02T09:00:00.000+02:00", "descrizione": "Apertura Pazienti · elenco" }] }
 */
exports.actionLogSchema = zod_1.z.strictObject({
    eventi: zod_1.z
        .array(zod_1.z.strictObject({
        orario: zod_1.z.iso.datetime({ offset: true, error: 'Orario: data e ora ISO 8601 con fuso' }),
        descrizione: zod_1.z
            .string({ error: 'Descrizione: campo obbligatorio' })
            .trim()
            .min(1, { error: 'Descrizione: campo obbligatorio' })
            .max(exports.ACTION_LOG_DESCRIPTION_MAX, {
            error: `Descrizione: troppo lunga (massimo ${exports.ACTION_LOG_DESCRIPTION_MAX} caratteri)`,
        }),
    }, { error: 'Evento: oggetto con orario e descrizione' }))
        .max(exports.ACTION_LOG_MAX_EVENTS, { error: `Eventi: al massimo ${exports.ACTION_LOG_MAX_EVENTS}` }),
}, { error: 'Log: oggetto con la lista «eventi»' });
/**
 * Stato di ciascun tipo di allegato di un ticket: l'**assenza dichiarata** (contratto §6), così chi
 * lavora il ticket non confonde «non arrivato» con «non previsto» o «rifiutato».
 */
exports.ATTACHMENT_STATUSES = [
    'custodito',
    'eliminato_per_retention',
    'rifiutato',
    'in_attesa',
    'non_inviato',
    'non_previsto',
];
exports.ATTACHMENT_STATUS_LABELS = {
    custodito: 'Custodito',
    eliminato_per_retention: 'Eliminato per retention',
    rifiutato: 'Rifiutato',
    in_attesa: 'In attesa del caricamento',
    non_inviato: 'Non inviato',
    non_previsto: 'Non previsto sul pre-accesso',
};
/** Accessi restituiti al più da `GET …/allegati/{id}/accessi`: i più recenti (il totale è a parte). */
exports.ATTACHMENT_ACCESSES_LIMIT = 100;
/** Codici di errore degli allegati, nello stile della ricezione dei ticket (`tickets.*`). */
exports.ATTACHMENT_ERROR_CODES = {
    /** Richiesta multipart senza una e una sola parte file `schermata` o `log`, o malformata (400). */
    invalidRequest: 'tickets.allegato_richiesta_non_valida',
    /** File oltre il limite del suo tipo (413). */
    tooLarge: 'tickets.allegato_troppo_grande',
    /** Schermata che non è PNG, JPEG o WebP secondo i primi byte (415). */
    unsupportedType: 'tickets.allegato_tipo_non_ammesso',
    /** Log non UTF-8, non JSON o fuori schema (422). */
    invalidLog: 'tickets.allegato_log_non_valido',
    /** Esiste già un allegato di quel tipo con un contenuto diverso (409). */
    alreadyPresent: 'tickets.allegato_gia_presente',
    /** Oltre i 15 minuti dalla ricezione del ticket (422). */
    windowExpired: 'tickets.allegato_finestra_scaduta',
    /** Archivio degli allegati non configurato o non raggiungibile (503): il ticket resta. */
    storageUnavailable: 'tickets.allegati_non_disponibili',
    /** Contenuto eliminato per retention: restano i metadati (410). */
    deletedForRetention: 'tickets.allegato_eliminato',
};
//# sourceMappingURL=attachments.js.map